/* verqo — Investor Pitch Deck (11 slides · 1920×1080)
   Rebuilt Feb 2026 to match the Genspark/Stan design brief:
     dark forest bg (#0b1914), chartreuse accent (#dfff3d),
     Bricolage Grotesque + DM Sans + JetBrains Mono type stack,
     one big idea per slide, 3-part vertical scaffold
     (header + body + footer) with 1px --line-3 dividers.
   Old long-scroll investor page preserved at /investors/legacy.
*/
import { useEffect, useRef, useState, useCallback } from "react";

const SLIDES = 11;
const SLIDE_LABELS = [
  "01 Cover", "02 Problem", "03 Solution", "04 How it works",
  "05 The Platform", "06 Why Now", "07 The Market", "08 The Business",
  "09 Trajectory", "10 Competition", "11 The Ask",
];
const FOOTER_LABELS = [
  "The modern operating system for wholesale",
  "The problem",
  "The solution",
  "How it works",
  "The platform",
  "Why now",
  "The market",
  "The business",
  "Trajectory · Year 1",
  "How we stack up",
  "The ask",
];

const STORAGE_KEY = "verqo-pitch-deck:index";

/* ────────────────────────── Deck Stage ────────────────────────── */

function useDeckStage() {
  const [index, setIndex] = useState(() => {
    if (typeof window === "undefined") return 0;
    const hash = window.location.hash.replace("#", "");
    const hashN = parseInt(hash, 10);
    if (!isNaN(hashN) && hashN >= 1 && hashN <= SLIDES) return hashN - 1;
    const stored = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (!isNaN(stored) && stored >= 0 && stored < SLIDES) return stored;
    return 0;
  });

  const go = useCallback((n) => {
    const next = Math.max(0, Math.min(SLIDES - 1, n));
    setIndex(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* noop */ }
    try { window.history.replaceState(null, "", `#${next + 1}`); } catch { /* noop */ }
    try { window.parent?.postMessage({ slideIndexChanged: next }, "*"); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      // Ignore when the user is typing in an input.
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (["ArrowRight", "PageDown", " "].includes(e.key)) { e.preventDefault(); go(index + 1); }
      else if (["ArrowLeft", "PageUp"].includes(e.key))    { e.preventDefault(); go(index - 1); }
      else if (e.key === "Home")                            { e.preventDefault(); go(0); }
      else if (e.key === "End")                             { e.preventDefault(); go(SLIDES - 1); }
      else if (e.key === "p" || e.key === "P")              { e.preventDefault(); window.print(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, go]);

  return { index, go };
}

function DeckStage({ index, children }) {
  // Scale 1920×1080 to fit viewport, letterbox on black.
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setScale(Math.min(w / 1920, h / 1080));
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  return (
    <div ref={wrapRef} className="deck-outer">
      <div
        className="deck-canvas"
        style={{ transform: `translate(-50%, -50%) scale(${scale})` }}
        data-testid="deck-canvas"
      >
        {Array.isArray(children)
          ? children.map((c, i) => (
              <div key={i} className={`slide-shell ${i === index ? "is-active" : ""}`}
                   aria-hidden={i !== index}
                   data-screen-label={SLIDE_LABELS[i]}
                   data-testid={`slide-${i + 1}`}>
                {c}
              </div>
            ))
          : children}
      </div>
    </div>
  );
}

/* ────────────────────────── Header / Footer ────────────────────────── */

function Header({ label }) {
  return (
    <div className="slide-header">
      <div className="brand">
        <div className="brand-mark">v</div>
        <div className="brand-name">verqo</div>
      </div>
      {label && <div className="mono-lime eyebrow"><span className="dot" />{label}</div>}
    </div>
  );
}

function Footer({ index, label }) {
  return (
    <div className="slide-footer">
      <div className="mono-dim">{String(index + 1).padStart(2, "0")} / {String(SLIDES).padStart(2, "0")}</div>
      <div className="mono-dim">{label}</div>
    </div>
  );
}

function Slide({ index, dense, glow, children }) {
  return (
    <section className={`slide ${dense ? "is-dense" : ""}`} data-dense={dense ? "true" : undefined}>
      {glow && <><span className="glow glow-tr" /><span className="glow glow-bl" /></>}
      <Header label={FOOTER_LABELS[index]} />
      <div className="slide-body">{children}</div>
      <Footer index={index} label={FOOTER_LABELS[index]} />
    </section>
  );
}

/* ────────────────────────── Slides ────────────────────────── */

function Cover() {
  return (
    <Slide index={0} glow>
      <div className="cover-body">
        <div className="mono-lime cover-eyebrow"><span className="dot" />The modern operating system for wholesale</div>
        <h1 className="cover-h1">
          Everything wholesale<br />
          needs to <span className="lime">sell, get paid,<br />and ship</span> — in one platform.
        </h1>
        <p className="cover-sub">
          Orders, payments, credit terms, shipping, and insurance in one platform.
          Same-day cash on every invoice. Zero default risk — Allianz insured.
          First invoice live in 48 hours.
        </p>
        <div className="cover-foot">
          <div className="mono-dim">verqo.com</div>
          <div className="mono-dim">Wholesale, instant.</div>
        </div>
      </div>
    </Slide>
  );
}

function Problem() {
  const stats = [
    { n: "60–90d", h: "Sellers wait for cash.",   d: "Invoices settle two months late. Inventory turn collapses." },
    { n: "7 tools", h: "None talk to each other.", d: "QuickBooks · Stripe · Shopify · BNPL · carrier · insurer · bank." },
    { n: "$3.1T",  h: "Trapped in receivables.",   d: "Working capital frozen across US wholesale, every year." },
  ];
  return (
    <Slide index={1}>
      <h2 className="display display-lg">
        $32 trillion of B2B trade still runs on <span className="lime">1980s plumbing.</span>
      </h2>
      <div className="grid-3" style={{ marginTop: 72 }}>
        {stats.map((s, i) => (
          <div key={i} className="card">
            <div className="big-num">{s.n}</div>
            <div className="card-h">{s.h}</div>
            <div className="card-d">{s.d}</div>
          </div>
        ))}
      </div>
    </Slide>
  );
}

function Solution() {
  const rows = [
    "Same-day cash on every invoice",
    "Zero default risk — Allianz insured",
    "First invoice live in 48 hours",
  ];
  return (
    <Slide index={2} dense>
      <div className="two-col" style={{ gap: 64, alignItems: "center" }}>
        <div style={{ maxWidth: 900 }}>
          <h2 className="display display-md">One platform.<br /><span className="lime">Every dollar.</span></h2>
          <p className="body-lg dim" style={{ maxWidth: 720, marginTop: 28 }}>
            Verqo replaces the seven-tool stack most wholesale brands live with today —
            one login for catalog, orders, invoicing, payments, credit terms, shipping and insurance.
          </p>
          <div className="ordered" style={{ marginTop: 40 }}>
            {rows.map((r, i) => (
              <div className="ordered-row" key={i}>
                <span className="ordered-num">{i + 1}</span>
                <span className="ordered-text">{r}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mockup">
          <div className="mockup-head">
            <div className="brand-mark sm">v</div>
            <div className="mockup-title">Invoice #INV-9087</div>
            <div className="chip"><span className="dot lime" />Funded</div>
          </div>
          <div className="mono-dim tiny">Amount funded to wallet</div>
          <div className="mockup-amt">$127,400</div>
          <div className="mockup-sub">Demo Wholesale Co. · Net-30 · 94% score</div>
          <div className="hr" />
          {[
            ["PO approved",       "09:14"],
            ["Credit approved · 60s", "09:15"],
            ["Allianz insured",   "09:15"],
            ["Cash in wallet",    "09:16"],
          ].map(([label, time], i) => (
            <div className="status-row" key={i}>
              <span className="dot lime" />
              <span className={`status-text ${i === 3 ? "bold" : ""}`}>{label}</span>
              <span className={`status-time mono ${i === 3 ? "lime" : ""}`}>{time}</span>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01 · Connect",       h: "Import your catalog & buyers.",  d: "Verqo credentials every buyer against the network in minutes." },
    { n: "02 · Sell on terms", h: "Buyers order on Net-30 / 60 / 90.", d: "Credit approved in under 60 seconds. You approve the PO." },
    { n: "03 · Get paid Day 1",h: "Full invoice hits your wallet.",  d: "Same-day, Allianz-insured. Ship from the same screen.", lime: true },
  ];
  const partners = ["Allianz Trade", "U.S. Bank", "Plaid", "Middesk", "Stripe", "Shopify"];
  return (
    <Slide index={3}>
      <h2 className="display display-lg">First funded invoice in <span className="lime">48 hours.</span></h2>
      <div className="grid-3" style={{ marginTop: 72 }}>
        {steps.map((s, i) => (
          <div key={i} className={`card ${s.lime ? "card-lime" : ""}`}>
            <div className="mono-lime step"><span className="dot" />{s.n}</div>
            <div className={`step-h ${s.lime ? "lime" : ""}`}>{s.h}</div>
            <div className="body-md dim step-d">{s.d}</div>
          </div>
        ))}
      </div>
      <div className="partners">
        <div className="mono-lime"><span className="dot" />Built on rails you already trust</div>
        <div className="partners-row">
          {partners.map((p, i) => (
            <span key={i} className="partner-name">
              {p}{i < partners.length - 1 && <span className="partner-sep" />}
            </span>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function Platform() {
  const cards = [
    { l: "Orders",    v: "Unified"          },
    { l: "Invoicing", v: "Auto-generated"   },
    { l: "Wallet",    v: "Ledger-backed"    },
    { l: "Credit",    v: "AI-underwritten", lime: true },
    { l: "Identity",  v: "KYB + KYC"        },
    { l: "Shipping",  v: "Smart-routed"     },
    { l: "Insurance", v: "Embedded"         },
    { l: "Apps",      v: "Extensible",      lime: true },
  ];
  return (
    <Slide index={4}>
      <div className="grid-bg" />
      <h2 className="display display-lg">One backbone.<br /><span className="lime">Every workflow.</span></h2>
      <p className="body-lg dim" style={{ maxWidth: 1000, marginTop: 24 }}>
        Every event — an order, a payment, a shipment, a credit decision — flows through
        the same ledger. One source of truth every workflow reads from.
      </p>
      <div className="orbit">
        <div className="hub">
          <div className="mono-lime tiny"><span className="dot" />One ledger</div>
          <div className="hub-name">verqo</div>
          <div className="mono-dim">source of truth</div>
        </div>
        <div className="orbit-grid">
          {cards.map((c, i) => (
            <div key={i} className={`orbit-card ${c.lime ? "card-lime" : ""}`}>
              <div className="mono-dim tiny">{c.l}</div>
              <div className={`orbit-val ${c.lime ? "lime" : ""}`}>{c.v}</div>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function WhyNow() {
  const items = [
    { n: "01", h: "AI underwriting",     d: "60-second credit decisions. Impossible 3 years ago." },
    { n: "02", h: "Embedded finance",    d: "Sponsor-bank rails, insurance APIs — now composable." },
    { n: "03", h: "Real-time payments",  d: "RTP + FedNow made same-day settlement table stakes." },
    { n: "04", h: "Modern APIs",          d: "Every ERP, warehouse, marketplace exposes clean APIs." },
  ];
  return (
    <Slide index={5}>
      <h2 className="display display-lg">Wholesale is having its<br /><span className="lime">Shopify moment.</span></h2>
      <p className="body-lg dim" style={{ maxWidth: 1200, marginTop: 24 }}>
        Four independent tech curves matured in the last 18 months. Together they
        make it possible — for the first time — to run wholesale like a modern platform.
      </p>
      <div className="grid-4" style={{ marginTop: 48 }}>
        {items.map((s, i) => (
          <div key={i} className="card">
            <div className="mono-lime step-sm"><span className="dot" />{s.n}</div>
            <div className="wynow-h">{s.h}</div>
            <div className="wynow-d body-md dim">{s.d}</div>
          </div>
        ))}
      </div>
      <div className="pull-quote">
        Shopify launched in 2006 when payments, open-source, and hosted retail
        infra all matured together. <span className="cream b">Wholesale&apos;s turn is now.</span>
      </div>
    </Slide>
  );
}

function Market() {
  const tam = [
    { n: "$32", u: "T", l: "Global B2B trade volume" },
    { n: "$1.45", u: "T", l: "US B2B BNPL TAM · 2028" },
    { n: "26",  u: "%", l: "CAGR B2B BNPL · 2024–28", lime: true },
    { n: "~7",  u: "%", l: "Of B2B trade fully digitized" },
  ];
  const pull = [
    { l: "Seller pull", v: "89%",    d: "of SMB suppliers cite slow payments as their #1 risk." },
    { l: "Buyer pull",  v: "64%",    d: "would buy more, more often, if Net-60 was instant." },
    { l: "Incumbent gap", v: "0 of 5", d: "top US B2B BNPLs serve both sides natively.", lime: true },
  ];
  return (
    <Slide index={6}>
      <h2 className="display display-lg">A $32T market.<br />Only <span className="lime">7% digitized.</span></h2>
      <div className="tam-row">
        {tam.map((t, i) => (
          <div key={i} className="tam-cell">
            <div className="tam-num">
              <span className={t.lime ? "lime" : ""}>{t.n}</span>
              <span className={`tam-unit ${t.lime ? "dim" : ""}`}>{t.u}</span>
            </div>
            <div className="mono-dim">{t.l}</div>
          </div>
        ))}
      </div>
      <div className="grid-3" style={{ marginTop: 40 }}>
        {pull.map((p, i) => (
          <div key={i} className={`card ${p.lime ? "card-lime" : ""}`}>
            <div className="mono-lime"><span className="dot" />{p.l}</div>
            <div className={`pull-v ${p.lime ? "lime" : ""}`}>{p.v}</div>
            <div className="body-md dim">{p.d}</div>
          </div>
        ))}
      </div>
    </Slide>
  );
}

function Business() {
  const rev = [
    { n: "01", h: "SaaS subscriptions",  d: "$99 → $1,999/mo" },
    { n: "02", h: "Financing markup",    d: "~25% on Net-X credit" },
    { n: "03", h: "Invoice factoring",   d: "~20% origination" },
    { n: "04", h: "Payment-rail spread", d: "ACH · wire · RTP" },
    { n: "05", h: "Card interchange",    d: "Net virtual-card" },
    { n: "06", h: "Insurance commission",d: "~0.5% insured GMV" },
    { n: "07", h: "Shipping margin",     d: "~10% commission" },
    { n: "08", h: "Apps & Catalog API",  d: "Seats + usage", lime: true },
  ];
  const metrics = [
    { l: "Blended take rate", v: "6.0%", lime: true },
    { l: "Gross margin",      v: "98.6%" },
    { l: "LTV : CAC",         v: "~18 : 1" },
    { l: "Payback",           v: "6.4 mo" },
  ];
  return (
    <Slide index={7} dense>
      <h2 className="display" style={{ fontSize: 88 }}>One transaction.<br /><span className="lime">Eight ways we earn.</span></h2>
      <p className="body-lg dim" style={{ maxWidth: 1100, marginTop: 18 }}>
        Every dollar that flows through Verqo touches multiple monetization surfaces.
        No single rail is fragile — the stack compounds.
      </p>
      <div className="grid-4 dense" style={{ marginTop: 32 }}>
        {rev.map((r, i) => (
          <div key={i} className={`card card-tight ${r.lime ? "card-lime" : ""}`}>
            <div className="mono-lime step-sm"><span className="dot" />{r.n}</div>
            <div className={`biz-h ${r.lime ? "lime" : ""}`}>{r.h}</div>
            <div className="body-sm dim">{r.d}</div>
          </div>
        ))}
      </div>
      <div className="metrics-strip">
        {metrics.map((m, i) => (
          <div key={i} className="metric">
            <div className="mono-dim">{m.l}</div>
            <div className={`metric-v ${m.lime ? "lime" : ""}`}>{m.v}</div>
          </div>
        ))}
      </div>
    </Slide>
  );
}

function Trajectory() {
  const kpi = [
    { n: "120",    l: "Sellers active EOY1" },
    { n: "300",    l: "Buyers onboarded" },
    { n: "$256M",  l: "GMV · Y1 total" },
    { n: "$863M",  l: "Bank-cleared · Y1", lime: true },
  ];
  const rows = [
    ["Sellers onboarded", "14",     "18",     "22",     "26"],
    ["GMV",               "$13.0M", "$23.8M", "$43.9M", "$63.7M"],
    ["Revenue",           "$3.4M",  "$12.8M", "$31.1M", "$45.6M"],
    ["Bank-cleared",      "$17.7M", "$87.2M", "$162.5M","$311.2M"],
  ];
  return (
    <Slide index={8} dense>
      <h2 className="display" style={{ fontSize: 88 }}><span className="lime">$863M</span> bank-cleared.<br />Built bottom-up.</h2>
      <div className="grid-4" style={{ marginTop: 40 }}>
        {kpi.map((k, i) => (
          <div key={i} className="kpi">
            <div className={`kpi-n ${k.lime ? "lime" : ""}`}>{k.n}</div>
            <div className="mono-dim">{k.l}</div>
          </div>
        ))}
      </div>
      <div className="table-card">
        <div className="mono-dim" style={{ marginBottom: 20 }}>Quarterly ramp · Year 1</div>
        <div className="ramp-table">
          <div className="ramp-row ramp-head">
            <div className="mono-dim sm">Metric</div>
            <div className="mono-dim sm">Q1</div>
            <div className="mono-dim sm">Q2</div>
            <div className="mono-dim sm">Q3</div>
            <div className="mono-dim sm">Q4</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className={`ramp-row ${i === rows.length - 1 ? "ramp-hi" : ""}`}>
              {r.map((cell, j) => (
                <div key={j} className={`ramp-cell ${j === 0 ? "ramp-label" : ""}`}>{cell}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function Competition() {
  const cols = ["verqo", "TreviPay", "Resolve", "Faire", "Shopify", "Wholesail"];
  // Matrix mirrors README: ● native, ◐ partial, — none.
  const rows = [
    ["Serves buyers AND sellers natively",       ["●","—","—","●","—","●"]],
    ["Owns the money rails (not a reseller)",    ["●","—","—","●","●","●"]],
    ["Embedded credit insurance (Allianz Trade)",["●","—","—","—","—","—"]],
    ["Multi-funder credit stack",                ["●","◐","—","—","●","●"]],
    ["Invoice factoring for sellers",            ["●","—","—","—","—","—"]],
    ["Embedded shipping & freight",              ["●","—","—","●","●","—"]],
    ["Bank-grade compliance suite",              ["●","●","◐","—","●","—"]],
  ];
  const cell = (v, isVerqo) => {
    if (v === "●") return <span className={`ind ind-yes ${isVerqo ? "b" : ""}`}>●</span>;
    if (v === "◐") return <span className="ind ind-partial">◐</span>;
    return <span className="ind ind-no">—</span>;
  };
  return (
    <Slide index={9} dense>
      <h2 className="display" style={{ fontSize: 72 }}>
        Everyone else picks a lane.<br /><span className="lime">We own the whole road.</span>
      </h2>
      <table className="compare" style={{ marginTop: 32 }}>
        <thead>
          <tr>
            <th className="mono-dim sm">Capability</th>
            {cols.map((c, i) => (
              <th key={i} className={`mono-dim sm ${i === 0 ? "col-verqo" : ""}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, cells], i) => (
            <tr key={i}>
              <td className="cap">{label}</td>
              {cells.map((c, j) => (
                <td key={j} className={j === 0 ? "col-verqo" : ""}>{cell(c, j === 0)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="legend">
        <div className="legend-item"><span className="ind ind-yes lg">●</span><span className="body-md">Native</span></div>
        <div className="legend-item"><span className="ind ind-partial lg">◐</span><span className="body-md">Partial / via partner</span></div>
        <div className="legend-item"><span className="ind ind-no lg">—</span><span className="body-md">Not offered</span></div>
      </div>
    </Slide>
  );
}

function Ask() {
  const growthBullets = [
    "→ 3 GTM hires · 4 engineering hires",
    "→ BSA officer + compliance lead",
    "→ 18-month runway to $1B GMV",
  ];
  const bankBullets = [
    "→ Collateralized against U.S. Bank",
    "→ Enables Day-0 payouts at scale",
    "→ Interest-bearing while idle",
  ];
  const milestones = [
    ["6 months",  "175 sellers · 3,500 buyers activated"],
    ["12 months", "$1B bank-cleared through U.S. Bank"],
    ["24 months", "Series A on our terms · EU+UK rollout"],
  ];
  const team = [
    ["Zev Ostreicher",     "Founder & CEO"],
    ["David Levy",         "Head of Operations"],
    ["Basi Hershkowitz",   "Sales Director"],
    ["Dom Byno",           "Data & Automation"],
  ];
  return (
    <Slide index={10} dense glow>
      <h2 className="display" style={{ fontSize: 88 }}><span className="lime">$5M Seed</span> to hit<br />$1B bank-cleared.</h2>
      <p className="body-lg dim" style={{ maxWidth: 1100, marginTop: 18 }}>
        Two-line ask: growth capital to hire and go-to-market, and a sponsor-bank
        reserve that unlocks Day-0 payouts at scale.
      </p>
      <div className="grid-2" style={{ marginTop: 32, gap: 24 }}>
        <div className="card">
          <div className="mono-dim">Growth capital</div>
          <div className="ask-v">$2M</div>
          <div className="mono-dim">Opex · Hiring · GTM</div>
          <div className="ask-bullets">
            {growthBullets.map((b, i) => <div key={i} className="body-md">{b}</div>)}
          </div>
        </div>
        <div className="card card-lime">
          <div className="mono-lime"><span className="dot" />Bank reserve</div>
          <div className="ask-v lime">$3M</div>
          <div className="mono-dim">Sponsor-bank collateral</div>
          <div className="ask-bullets">
            {bankBullets.map((b, i) => <div key={i} className="body-md">{b}</div>)}
          </div>
        </div>
      </div>
      <div className="ask-strip">
        <div className="ask-col">
          <div className="mono-lime"><span className="dot" />Milestones</div>
          <div className="ms">
            {milestones.map(([t, d], i) => (
              <div key={i} className="ms-row"><span className="ms-t lime">{t}</span><span className="ms-d">{d}</span></div>
            ))}
          </div>
        </div>
        <div className="ask-div" />
        <div className="ask-col">
          <div className="mono-lime"><span className="dot" />Team</div>
          <div className="team-grid">
            {team.map(([n, r], i) => (
              <div key={i} className="team-cell"><div className="team-n">{n}</div><div className="team-r">{r}</div></div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
}

/* ────────────────────────── Chrome (nav + hint) ────────────────────────── */

function DeckChrome({ index, go }) {
  const [hint, setHint] = useState(true);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setHint(false), 4000);
    return () => clearTimeout(t);
  }, []);
  // Auto-hide the chrome when idle so it never obscures dense slide content;
  // reveal it again on any pointer/keyboard activity (standard deck behavior).
  useEffect(() => {
    let timer;
    const wake = () => {
      setVisible(true);
      clearTimeout(timer);
      timer = setTimeout(() => setVisible(false), 2500);
    };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("keydown", wake);
    window.addEventListener("touchstart", wake);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("keydown", wake);
      window.removeEventListener("touchstart", wake);
    };
  }, []);
  return (
    <div className={`deck-chrome ${visible ? "is-visible" : ""}`}>
      <div className="chrome-progress">
        <div className="chrome-bar" style={{ width: `${((index + 1) / SLIDES) * 100}%` }} />
      </div>
      <div className="chrome-row">
        <button className="chrome-btn" onClick={() => go(index - 1)} disabled={index === 0}
                data-testid="deck-prev" aria-label="Previous slide">←</button>
        <div className="chrome-idx mono-dim">{String(index + 1).padStart(2, "0")} / {String(SLIDES).padStart(2, "0")}</div>
        <button className="chrome-btn" onClick={() => go(index + 1)} disabled={index === SLIDES - 1}
                data-testid="deck-next" aria-label="Next slide">→</button>
        <button className="chrome-btn wide" onClick={() => window.print()}
                data-testid="deck-print" aria-label="Print to PDF">Print PDF</button>
      </div>
      {hint && <div className="chrome-hint mono-dim">← → to navigate · P to print</div>}
    </div>
  );
}

/* ────────────────────────── Page ────────────────────────── */

export default function InvestorPitch() {
  const { index, go } = useDeckStage();

  // Google Fonts injection (runs once).
  useEffect(() => {
    const id = "verqo-deck-fonts";
    if (document.getElementById(id)) return;
    const pre1 = document.createElement("link"); pre1.rel = "preconnect"; pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link"); pre2.rel = "preconnect"; pre2.href = "https://fonts.gstatic.com"; pre2.crossOrigin = "anonymous";
    const link = document.createElement("link"); link.rel = "stylesheet"; link.id = id;
    link.href = "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=JetBrains+Mono:wght@400;500;600&display=swap";
    document.head.append(pre1, pre2, link);
  }, []);

  return (
    <>
      <style>{deckStyles}</style>
      <DeckStage index={index}>
        {[
          <Cover key="cover"/>,
          <Problem key="problem"/>,
          <Solution key="solution"/>,
          <HowItWorks key="how"/>,
          <Platform key="platform"/>,
          <WhyNow key="why"/>,
          <Market key="market"/>,
          <Business key="biz"/>,
          <Trajectory key="traj"/>,
          <Competition key="comp"/>,
          <Ask key="ask"/>,
        ]}
      </DeckStage>
      <DeckChrome index={index} go={go} />
    </>
  );
}

/* ────────────────────────── Stylesheet ────────────────────────── */

const deckStyles = `
:root {
  --ink: #0b1914;
  --ink-2: #0a1f1c;
  --cream: #f7f6f3;
  --dim: rgba(247,246,243,0.62);
  --dim-2: rgba(247,246,243,0.42);
  --lime: #dfff3d;
  --lime-soft: rgba(223,255,61,0.10);
  --line: rgba(223,255,61,0.14);
  --line-2: rgba(255,255,255,0.10);
  --line-3: rgba(255,255,255,0.06);
  --partial: rgba(245,158,11,0.85);
  --font-display: "Bricolage Grotesque", ui-sans-serif, system-ui, sans-serif;
  --font-body: "DM Sans", ui-sans-serif, system-ui, sans-serif;
  --font-mono: "JetBrains Mono", ui-monospace, "SF Mono", monospace;
}

.deck-outer {
  position: fixed; inset: 0;
  background: #000;
  overflow: hidden;
  font-family: var(--font-body);
  color: var(--cream);
}
.deck-canvas {
  position: absolute; top: 50%; left: 50%;
  width: 1920px; height: 1080px;
  transform-origin: center center;
}
.slide-shell { position: absolute; inset: 0; opacity: 0; pointer-events: none; transition: opacity .3s ease; }
.slide-shell.is-active { opacity: 1; pointer-events: auto; }

.slide {
  position: absolute; inset: 0;
  background: var(--ink);
  color: var(--cream);
  padding: 56px 96px;
  display: flex; flex-direction: column;
  overflow: hidden;
  font-family: var(--font-body);
}

.slide-header {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--line-3);
  min-height: 76px;
}
.brand { display: flex; align-items: center; gap: 16px; }
.brand-mark {
  width: 56px; height: 56px; border-radius: 12px;
  border: 1.5px solid var(--lime);
  color: var(--lime);
  display: grid; place-items: center;
  font-family: var(--font-display); font-weight: 500;
  font-size: 34px; line-height: 1;
}
.brand-mark.sm { width: 22px; height: 22px; border-radius: 5px; font-size: 14px; border-width: 1px; }
.brand-name { font-family: var(--font-body); font-weight: 500; font-size: 34px; letter-spacing: -0.015em; color: var(--cream); }

.eyebrow { display: inline-flex; align-items: center; gap: 10px; }
.mono, .mono-lime, .mono-dim {
  font-family: var(--font-mono); font-weight: 500;
  letter-spacing: 0.14em; text-transform: uppercase;
}
.mono-lime { color: var(--lime); font-size: 18px; }
.mono-dim  { color: var(--dim-2); font-size: 18px; }
.mono-dim.sm { font-size: 12px; }
.mono-dim.tiny { font-size: 12px; }
.mono-lime.tiny { font-size: 11px; }

.dot { width: 6px; height: 6px; border-radius: 50%; background: var(--lime); display: inline-block; }
.dot.lime { background: var(--lime); }

.slide-body {
  flex: 1; display: flex; flex-direction: column;
  padding-top: 40px; padding-bottom: 24px;
  position: relative;
}
.slide.is-dense .slide-body { padding-top: 28px; padding-bottom: 16px; }

.slide-footer {
  display: flex; justify-content: space-between;
  padding-top: 20px; margin-top: 32px;
  border-top: 1px solid var(--line-3);
  min-height: 50px;
}
.slide.is-dense .slide-footer { margin-top: 20px; }

/* Typography ------------------------------------------------------ */
.display { font-family: var(--font-display); font-weight: 500; letter-spacing: -0.025em; line-height: 0.98; color: var(--cream); }
.display-lg { font-size: 112px; font-weight: 600; }
.display-md { font-size: 84px;  font-weight: 600; }
.display-sm { font-size: 64px;  font-weight: 600; }

.body-lg { font-family: var(--font-body); font-size: 26px; line-height: 1.45; letter-spacing: -0.005em; }
.body-md { font-family: var(--font-body); font-size: 20px; line-height: 1.5; }
.body-sm { font-family: var(--font-body); font-size: 16px; line-height: 1.5; }

.big-num { font-family: var(--font-display); font-weight: 500; letter-spacing: -0.03em; line-height: 0.9; font-size: 88px; color: var(--cream); }

.dim { color: var(--dim); }
.lime { color: var(--lime); }
.cream { color: var(--cream); }
.b { font-weight: 600; }

/* Glows ------------------------------------------------------------ */
.glow {
  position: absolute; pointer-events: none;
  width: 560px; height: 560px;
  background: radial-gradient(closest-side, rgba(223,255,61,0.22), transparent 70%);
  filter: blur(20px);
}
.glow-tr { top: -180px; right: -180px; }
.glow-bl { bottom: -180px; left: -180px; }

/* Cover ------------------------------------------------------------ */
.cover-body { display: flex; flex-direction: column; height: 100%; }
.cover-eyebrow { margin-top: 24px; }
.cover-h1 {
  font-family: var(--font-display); font-weight: 600;
  font-size: 140px; line-height: 1.02;
  letter-spacing: -0.025em;
  color: var(--cream);
  max-width: 1720px;
  margin-top: 40px;
}
.cover-sub { font-family: var(--font-body); font-size: 32px; line-height: 1.4; color: var(--dim); max-width: 1200px; margin-top: 40px; }
.cover-foot { margin-top: auto; display: flex; justify-content: space-between; }

/* Cards & grids ---------------------------------------------------- */
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; }
.grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 24px; }
.grid-4.dense { gap: 16px; }
.two-col { display: grid; grid-template-columns: 1fr 560px; }

.card {
  background: var(--ink-2);
  border: 1px solid var(--line-3);
  border-radius: 16px;
  padding: 32px;
  display: flex; flex-direction: column; gap: 12px;
}
.card-tight { padding: 24px; gap: 8px; }
.card-lime { background: var(--lime-soft); border-color: var(--line); }
.card .card-h { font-family: var(--font-display); font-weight: 500; font-size: 30px; letter-spacing: -0.015em; color: var(--cream); margin-top: 12px; }
.card .card-d { font-family: var(--font-body); font-size: 17px; color: var(--dim); line-height: 1.5; }

/* Problem stats */
.card .big-num { margin-bottom: 4px; }

/* Solution mockup ------------------------------------------------- */
.ordered { display: flex; flex-direction: column; gap: 20px; }
.ordered-row { display: flex; align-items: center; gap: 20px; }
.ordered-num {
  width: 28px; height: 28px; border-radius: 6px;
  background: var(--lime); color: var(--ink);
  font-family: var(--font-display); font-weight: 600; font-size: 16px;
  display: grid; place-items: center;
}
.ordered-text { font-family: var(--font-body); font-size: 21px; font-weight: 500; color: var(--cream); }

.mockup {
  background: var(--ink-2);
  border: 1px solid var(--line-2);
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 40px 100px rgba(0,0,0,0.5), 0 0 0 1px rgba(223,255,61,0.06) inset;
  width: 560px;
}
.mockup-head { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
.mockup-title { font-family: var(--font-body); font-weight: 500; font-size: 18px; color: var(--cream); flex: 1; }
.chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px; border-radius: 999px;
  border: 1px solid var(--line-2);
  font-family: var(--font-mono); font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--cream);
}
.mockup-amt { font-family: var(--font-display); font-weight: 600; font-size: 56px; color: var(--cream); line-height: 1; margin-top: 4px; }
.mockup-sub { font-family: var(--font-body); font-size: 16px; color: var(--dim); margin-top: 8px; }
.hr { border-top: 1px solid var(--line-3); margin: 20px 0 14px 0; }
.status-row { display: flex; align-items: center; gap: 12px; padding: 8px 0; }
.status-text { flex: 1; font-family: var(--font-body); font-size: 14px; color: var(--cream); }
.status-text.bold { font-weight: 600; }
.status-time { font-family: var(--font-mono); font-size: 12px; color: var(--dim-2); }
.status-time.lime { color: var(--lime); }

/* How it works ---------------------------------------------------- */
.step { display: inline-flex; align-items: center; gap: 10px; font-size: 14px; }
.step-sm { display: inline-flex; align-items: center; gap: 10px; font-size: 14px; }
.step-h { font-family: var(--font-display); font-weight: 500; font-size: 52px; line-height: 1.02; letter-spacing: -0.02em; color: var(--cream); margin-top: 16px; }
.step-d { margin-top: 8px; }
.partners { margin-top: 64px; display: flex; flex-direction: column; gap: 20px; }
.partners-row { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; opacity: .85; }
.partner-name { font-family: var(--font-display); font-weight: 500; font-size: 26px; letter-spacing: -0.01em; color: var(--cream); opacity: .75; display: inline-flex; align-items: center; gap: 24px; }
.partner-sep { width: 6px; height: 6px; border-radius: 50%; background: var(--dim-2); display: inline-block; }

/* Platform (orbit) ------------------------------------------------ */
.grid-bg {
  position: absolute; inset: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 80px 80px;
}
.orbit { position: relative; margin-top: 40px; height: 480px; display: grid; place-items: center; }
.hub {
  width: 280px; height: 280px; border-radius: 50%;
  border: 1.5px solid var(--lime);
  background: radial-gradient(closest-side, rgba(223,255,61,0.12), rgba(11,25,20,0.4) 80%);
  display: grid; place-items: center; gap: 6px;
  text-align: center;
}
.hub-name { font-family: var(--font-display); font-weight: 500; font-size: 44px; color: var(--cream); line-height: 1; }
.orbit-grid {
  position: absolute; inset: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 1fr 280px 1fr;
  gap: 24px;
  align-items: stretch;
}
.orbit-card {
  background: var(--ink-2);
  border: 1px solid var(--line-3);
  border-radius: 16px;
  padding: 20px 24px;
  display: flex; flex-direction: column; gap: 6px; justify-content: center;
}
.orbit-card:nth-child(1) { grid-column: 1; grid-row: 1; }
.orbit-card:nth-child(2) { grid-column: 2; grid-row: 1; }
.orbit-card:nth-child(3) { grid-column: 3; grid-row: 1; }
.orbit-card:nth-child(4) { grid-column: 4; grid-row: 1; }
.orbit-card:nth-child(5) { grid-column: 1; grid-row: 3; }
.orbit-card:nth-child(6) { grid-column: 2; grid-row: 3; }
.orbit-card:nth-child(7) { grid-column: 3; grid-row: 3; }
.orbit-card:nth-child(8) { grid-column: 4; grid-row: 3; }
.orbit-val { font-family: var(--font-display); font-weight: 500; font-size: 28px; letter-spacing: -0.015em; color: var(--cream); }

/* Why now --------------------------------------------------------- */
.wynow-h { font-family: var(--font-display); font-weight: 500; font-size: 34px; letter-spacing: -0.015em; color: var(--cream); margin-top: 16px; line-height: 1.05; }
.wynow-d { margin-top: 8px; }
.pull-quote {
  margin-top: 56px;
  padding: 24px 32px;
  border-left: 2px solid var(--lime);
  background: var(--lime-soft);
  border-radius: 4px 12px 12px 4px;
  font-family: var(--font-body); font-size: 22px; color: var(--cream); line-height: 1.4;
}

/* Market ---------------------------------------------------------- */
.tam-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; margin-top: 48px; }
.tam-cell { display: flex; flex-direction: column; gap: 8px; }
.tam-num { font-family: var(--font-display); font-weight: 500; font-size: 110px; line-height: 0.9; letter-spacing: -0.03em; color: var(--cream); display: inline-flex; align-items: baseline; }
.tam-unit { font-size: 60px; margin-left: 6px; color: var(--cream); }
.tam-unit.dim { color: var(--dim-2); }
.pull-v { font-family: var(--font-display); font-weight: 500; font-size: 64px; line-height: 1; letter-spacing: -0.02em; color: var(--cream); margin-top: 8px; }

/* Business -------------------------------------------------------- */
.biz-h { font-family: var(--font-display); font-weight: 500; font-size: 24px; letter-spacing: -0.01em; color: var(--cream); margin-top: 8px; }
.metrics-strip { margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--line-3); display: grid; grid-template-columns: repeat(4, 1fr); column-gap: 48px; }
.metric { display: flex; flex-direction: column; gap: 8px; }
.metric-v { font-family: var(--font-display); font-weight: 500; font-size: 40px; letter-spacing: -0.02em; color: var(--cream); line-height: 1; }
.metric-v.lime { color: var(--lime); }

/* Trajectory ------------------------------------------------------ */
.kpi { display: flex; flex-direction: column; gap: 8px; }
.kpi-n { font-family: var(--font-display); font-weight: 500; font-size: 84px; letter-spacing: -0.03em; color: var(--cream); line-height: 0.9; }
.kpi-n.lime { color: var(--lime); }
.table-card { margin-top: 40px; padding: 24px 28px; background: var(--ink-2); border: 1px solid var(--line-3); border-radius: 16px; }
.ramp-table { display: flex; flex-direction: column; }
.ramp-row { display: grid; grid-template-columns: 1.4fr repeat(4, 1fr); padding: 16px 0; border-bottom: 1px solid var(--line-3); align-items: center; }
.ramp-row:last-child { border-bottom: none; }
.ramp-row.ramp-head { padding: 8px 0 16px 0; }
.ramp-cell { font-family: var(--font-display); font-weight: 400; font-size: 22px; color: var(--cream); }
.ramp-cell.ramp-label { font-family: var(--font-body); font-size: 15px; color: var(--dim); font-weight: 500; text-transform: uppercase; letter-spacing: 0.1em; }
.ramp-row.ramp-hi .ramp-cell { color: var(--lime); font-weight: 500; }
.ramp-row.ramp-hi .ramp-cell.ramp-label { color: var(--lime); }

/* Competition ----------------------------------------------------- */
.compare { width: 100%; border-collapse: collapse; }
.compare th, .compare td { text-align: left; padding: 18px 20px; border-bottom: 1px solid var(--line-3); font-size: 24px; color: var(--cream); }
.compare th { text-align: left; font-size: 15px; }
.compare th.col-verqo { color: var(--lime); font-size: 18px; }
.compare td.cap { font-family: var(--font-body); font-size: 20px; color: var(--cream); }
.compare .col-verqo { background: rgba(223,255,61,0.05); }
.compare th.col-verqo, .compare td.col-verqo { text-align: center; }
.compare th:not(.col-verqo):not(:first-child) { text-align: center; }
.compare td:not(.cap):not(.col-verqo) { text-align: center; }

.ind { font-family: var(--font-mono); font-size: 30px; }
.ind-yes { color: var(--lime); }
.ind-yes.b { font-weight: 600; }
.ind-partial { color: var(--partial); }
.ind-no { color: rgba(255,255,255,0.20); }
.ind.lg { font-size: 22px; }
.legend { display: flex; gap: 32px; margin-top: 24px; }
.legend-item { display: inline-flex; align-items: center; gap: 12px; }

/* Ask ------------------------------------------------------------- */
.ask-v { font-family: var(--font-display); font-weight: 500; font-size: 56px; letter-spacing: -0.02em; color: var(--cream); line-height: 1; margin-top: 8px; }
.ask-v.lime { color: var(--lime); }
.ask-bullets { display: flex; flex-direction: column; gap: 10px; margin-top: 20px; }
.ask-strip { display: grid; grid-template-columns: 1fr 1px 1fr; gap: 32px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--line-3); }
.ask-col { display: flex; flex-direction: column; gap: 16px; }
.ask-div { background: var(--line-3); width: 1px; }
.ms { display: flex; flex-direction: column; gap: 12px; }
.ms-row { display: grid; grid-template-columns: 110px 1fr; gap: 20px; align-items: center; }
.ms-t { font-family: var(--font-display); font-weight: 500; font-size: 20px; color: var(--lime); }
.ms-d { font-family: var(--font-body); font-size: 18px; color: var(--cream); }
.team-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 24px; }
.team-cell { display: flex; flex-direction: column; gap: 2px; }
.team-n { font-family: var(--font-body); font-weight: 600; font-size: 17px; color: var(--cream); }
.team-r { font-family: var(--font-body); font-size: 14px; color: var(--dim); }

/* Deck chrome (navigation UI outside the canvas) ------------------ */
.deck-chrome {
  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
  z-index: 50;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  pointer-events: none;
  opacity: 0; transition: opacity .35s ease;
}
.deck-chrome.is-visible { opacity: 1; }
.chrome-progress { width: 260px; height: 3px; background: rgba(255,255,255,0.08); border-radius: 999px; overflow: hidden; }
.chrome-bar { height: 100%; background: var(--lime); transition: width .3s ease; }
.chrome-row { pointer-events: auto; display: flex; align-items: center; gap: 8px; background: rgba(11,25,20,0.85); backdrop-filter: blur(8px); border: 1px solid var(--line-3); border-radius: 999px; padding: 6px; }
.chrome-btn {
  width: 40px; height: 40px; border-radius: 999px;
  background: transparent; border: none; color: var(--cream);
  font-family: var(--font-mono); font-size: 16px; cursor: pointer;
  display: grid; place-items: center;
  transition: background .15s ease;
}
.chrome-btn:hover:not(:disabled) { background: rgba(223,255,61,0.10); }
.chrome-btn:disabled { color: var(--dim-2); cursor: not-allowed; }
.chrome-btn.wide { width: auto; padding: 0 16px; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--lime); }
.chrome-idx { padding: 0 10px; font-size: 12px; }
.chrome-hint { font-size: 11px; opacity: .6; pointer-events: none; }

/* Print ----------------------------------------------------------- */
@media print {
  .deck-outer { position: static; background: #fff; }
  .deck-canvas { position: static; transform: none !important; width: 1920px; height: auto; }
  .slide-shell { position: static !important; opacity: 1 !important; page-break-after: always; break-after: page; }
  .slide-shell:last-child { page-break-after: auto; }
  .deck-chrome { display: none !important; }
  .slide { position: relative; width: 1920px; height: 1080px; }
}
`;
